import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot } from '../firebase.config';
import { db } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getAuth } from 'firebase/auth';
import { useUnreadReports } from '../hooks/useUnreadReports';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const ReportToOwner = () => {
  const { currentCompany } = useEnhancedCompany();
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const { markEmployeeReplyAsRead } = useUnreadReports();

  // Form states
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [billNo, setBillNo] = useState('');
  const [reportType, setReportType] = useState('');
  const [message, setMessage] = useState('');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  // currentTime state removed - no longer needed for countdown

  // Page and subcategory options
  const pageOptions = {
    'Business': {
      'Sales': ['Invoice', 'Challan', 'Quotation'],
      'Purchases': ['Purchase Bill', 'Purchase Order'],
      'Payments': ['Invoice', 'Challan', 'Purchase', 'Payment Receipts', 'Payment Mode'],
      'Expenses': ['Fixed', 'Variable', 'Labour/Advances', 'Employees', 'Salary'],
      'Manufacturing': ['Production Orders', 'Process Definitions', 'Process Directory', 'Analytics']
    },
    'Management': {
      'Items': [],
      'Parties': ['Parties Information', 'Parties Outstandings', 'Opening Balance Management'],
      'Bill Templates': []
    },
    'Reports': {
      'Business Reports': ['Partywise Sales Report', 'Customer Ledger Report', 'Invoice Collection Report', 'Payment Register Report', 'Aging Report', 'Itemwise Sales Report', 'Purchase Bills Summary', 'Stock Report'],
      'Tax Reports': ['GST Summary Report', 'GSTR-1 Report', 'GSTR-3B Report', 'HSN-wise GST Summary', 'Tax Payments Report'],
      'Financial Reports': ['Profit & Loss Report', 'Balance Sheet Report', 'Cash Flow Report'],
      'Data Export': ['Data Export Tools']
    },
    'System': {
      'Settings': ['Profile', 'Security & MPIN', 'MPIN Access Control', 'Payments', 'SMS', 'Branding'],
      'Company': ['Company Details', 'Company Settings'],
      'Help & Support': ['Help', 'FAQ', 'Contact Support'],
      'Image Manager': ['Image Management']
    }
  };

  // Get subcategories based on selected page
  const getSubCategories = () => {
    if (!selectedPage) return [];
    for (const category in pageOptions) {
      if (pageOptions[category][selectedPage]) {
        return pageOptions[category][selectedPage];
      }
    }
    return [];
  };

  // Check if selected page requires bill number or report type
  const needsBillNo = () => {
    const billRequiredPages = ['Sales', 'Purchases', 'Payments', 'Expenses'];
    return billRequiredPages.includes(selectedPage);
  };

  const needsReportType = () => {
    return selectedPage === 'Reports';
  };

  // Load reports for current user
  useEffect(() => {
    if (!currentCompany?.companyId || !currentUser?.uid) {
      console.log('🔍 ReportToOwner: Missing data:', { 
        companyId: currentCompany?.companyId, 
        userId: currentUser?.uid 
      });
      return;
    }

    console.log('🔍 ReportToOwner: Loading reports for:', {
      companyId: currentCompany.companyId,
      userId: currentUser.uid
    });

    const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
    const q = query(
      reportsRef,
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by createdAt locally to avoid index requirement
      reportsData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      console.log('📋 Loaded reports for employee:', reportsData.length, reportsData);
      setReports(reportsData);

      // Mark unread replies as read when component loads
      reportsData.forEach(report => {
        if (report.ownerReply && !report.employeeReadReply) {
          markEmployeeReplyAsRead(report.id);
        }
      });

      // Check for escalation (24 hours = 24 * 60 * 60 * 1000 milliseconds)
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));

      for (const report of reportsData) {
        // Auto-escalate pending reports after 24 hours
        if (!report.ownerReply && !report.escalated && report.createdAt.toDate() < twentyFourHoursAgo) {
          try {
            const reportRef = doc(db, 'companies', currentCompany.companyId, 'reports', report.id);
            await updateDoc(reportRef, {
              escalated: true,
              escalatedAt: new Date(),
              escalationReminderSent: true, // Mark as reminder sent
              status: 'escalated',
              updatedAt: new Date()
            });
          } catch (error) {
            console.error('Error escalating report:', error);
          }
        }

        // Resolved reports now stay resolved - no auto-archiving
      }
    }, (error) => {
      console.error('Error loading employee reports:', error);
    });

    return () => unsubscribe();
  }, [currentCompany?.companyId, currentUser?.uid, markEmployeeReplyAsRead]);

  // Countdown functionality removed - resolved issues stay resolved

  // Submit report
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPage || !currentCompany?.companyId || !currentUser?.uid) return;

    setLoading(true);
    try {
      const reportData = {
        userId: currentUser.uid,
        employeeId: currentUser.uid, // Will be updated when employee system is integrated
        companyId: currentCompany.id,
        page: selectedPage,
        subCategory: selectedSubCategory || '',
        billNo: billNo || '',
        reportType: reportType || '',
        message: message || '',
        status: 'pending',
        createdAt: new Date(),
        updatedAt: new Date(),
        isRead: false,
        ownerReply: '',
        ownerReplyAt: null,
        employeeReadReply: false,
        escalated: false,
        escalatedAt: null,
        escalationReminderSent: false
      };

      if (editingReport) {
        // Update existing report
        const reportRef = doc(db, 'companies', currentCompany.id, 'reports', editingReport.id);
        await updateDoc(reportRef, {
          ...reportData,
          updatedAt: new Date()
        });
        setEditingReport(null);
      } else {
        // Create new report
        await addDoc(collection(db, 'companies', currentCompany.id, 'reports'), reportData);
      }

      // Reset form
      setSelectedPage('');
      setSelectedSubCategory('');
      setBillNo('');
      setReportType('');
      setMessage('');

    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Error submitting report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Edit report
  const handleEdit = (report) => {
    setEditingReport(report);
    setSelectedPage(report.page);
    setSelectedSubCategory(report.subCategory);
    setBillNo(report.billNo);
    setReportType(report.reportType);
    setMessage(report.message);
  };

  // Delete report
  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      await deleteDoc(doc(db, 'companies', currentCompany.id, 'reports', reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Error deleting report. Please try again.');
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditingReport(null);
    setSelectedPage('');
    setSelectedSubCategory('');
    setBillNo('');
    setReportType('');
    setMessage('');
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Countdown functionality removed - resolved issues stay resolved

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Report to Owner</h1>
        <p className="text-gray-600">Submit issues or problems to the owner for resolution</p>
      </div>

      {/* Report Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {editingReport ? 'Edit Report' : 'New Report'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Page Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Page/Feature <span className="text-red-500">*</span>
            </label>
            <AppSelect
              value={selectedPage}
              onChange={(e) => {
                setSelectedPage(e.target.value);
                setSelectedSubCategory('');
                setBillNo('');
                setReportType('');
              }}
              placeholder="Select Page/Feature"
              aria-label="Page/Feature"
              sheetTitle="Page/Feature"
              options={Object.keys(pageOptions).flatMap(category =>
                Object.keys(pageOptions[category]).map(page => ({
                  value: page,
                  label: page,
                }))
              )}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
            />
          </div>

          {/* Sub-category Selection */}
          {getSubCategories().length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specific Section
              </label>
              <AppSelect
                value={selectedSubCategory}
                onChange={(e) => setSelectedSubCategory(e.target.value)}
                placeholder="Select Section (Optional)"
                aria-label="Specific section"
                sheetTitle="Specific section"
                options={getSubCategories().map(sub => ({ value: sub, label: sub }))}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
              />
            </div>
          )}

          {/* Bill Number Input */}
          {needsBillNo() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bill Number / Reference
              </label>
              <input
                type="text"
                value={billNo}
                onChange={(e) => setBillNo(e.target.value)}
                placeholder="Enter bill number or reference"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* Report Type Selection */}
          {needsReportType() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </label>
              <AppSelect
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                placeholder="Select Report Type"
                aria-label="Report type"
                sheetTitle="Report type"
                options={Object.values(pageOptions.Reports).flat().map(report => ({
                  value: report,
                  label: report,
                }))}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
              />
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message / Description
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue or problem in detail"
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading || !selectedPage}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : editingReport ? 'Update Report' : 'Send to Owner'}
            </button>

            {editingReport && (
              <button
                type="button"
                onClick={handleCancel}
                className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Your Reports</h2>
        </div>

        <div className="overflow-x-auto">
          {reports.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No reports submitted yet
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page/Feature
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner Reply
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.page}
                      {report.subCategory && <div className="text-xs text-gray-500">{report.subCategory}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.billNo && <div>Bill: {report.billNo}</div>}
                      {report.reportType && <div>Report: {report.reportType}</div>}
                      {report.message && <div className="text-xs text-gray-600 mt-1">{report.message}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(report.status)}`}>
                        {report.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {report.status === 'resolved' ? (
                        <span className="text-green-600 font-medium">Resolved</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {report.ownerReply ? (
                        <div>
                          <div className="text-blue-600">{report.ownerReply}</div>
                          {report.ownerReplyAt && (
                            <div className="text-xs text-gray-500">
                              {new Date(report.ownerReplyAt.seconds * 1000).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No reply yet</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(report.createdAt.seconds * 1000).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(report)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        disabled={report.ownerReply}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="text-red-600 hover:text-red-900"
                        disabled={report.ownerReply}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportToOwner;
